import { useId, useState } from 'react'
import type { KeyboardEvent, ReactElement } from 'react'

import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpcMessage } from '../../lib/trpcMessages'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { Button } from '../Button/Button'
import { CodeLabel } from '../CodeLabel/CodeLabel'
import styles from './itemPicker.module.scss'

type SearchedItem = RouterOutputs['configuration']['searchItems']['data'][number]

/**
 * An item as a configuration row references it: its code, and what the mirror
 * says about it. The `ItemReference` every configuration list answers has this
 * shape, so a row being edited seeds the picker as it is — orphan included.
 */
export type PickedItem = {
  code: string
  title: string | null
  groupTitle: string | null
  known: boolean
}

export type ItemPickerProps = {
  label: string
  /** The chosen item, or null while there is none. */
  selected: PickedItem | null
  onSelect: (item: PickedItem | null) => void
  hint?: string
  disabled?: boolean
  /** Offer «Quitar» on a chosen item: for an optional code, where none is a valid answer. */
  clearable?: boolean
}

/** Waits this long after the last keystroke before asking: one request per pause, not per key. */
export const ITEM_SEARCH_DEBOUNCE_MS = 250

/** A page of matches: enough to recognise the one meant, never the 5.000-item catalogue. */
const SEARCH_LIMIT = 20

/** A search hit the configuration can reference: the mirror's `code` is nullable, and a code is what gets stored. */
const toPicked = (item: SearchedItem): PickedItem | null =>
  item.code === null ? null : { code: item.code, title: item.title, groupTitle: item.groupTitle, known: true }

/**
 * Picks an item of the mirror (`mrp.items`) by code or title, searching on the
 * server as one types — the catalogue is thousands of rows and never travels
 * whole. A combobox in the ARIA sense: arrows move through the matches, Enter
 * takes one, Esc closes the list.
 *
 * With an item chosen, the field becomes that item and a «Cambiar» button: on a
 * tablet a chosen value that looks like a search box gets typed over by mistake.
 */
export const ItemPicker = ({
  label,
  selected,
  onSelect,
  hint,
  disabled = false,
  clearable = false,
}: ItemPickerProps): ReactElement => {
  const baseId = useId()
  const inputId = `${baseId}-input`
  const listId = `${baseId}-list`
  const hintId = `${baseId}-hint`

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const term = useDebouncedValue(query.trim(), ITEM_SEARCH_DEBOUNCE_MS)

  // The API refuses an empty query (a blank search would be the catalogue), so a
  // whitespace-only field asks nothing at all.
  const search = trpc.configuration.searchItems.useQuery(
    { query: term, limit: SEARCH_LIMIT },
    { enabled: term !== '' && !disabled && selected === null },
  )
  const matches = (search.data?.data ?? [])
    .map(toPicked)
    .filter((item): item is PickedItem => item !== null)

  const pick = (item: PickedItem): void => {
    onSelect(item)
    setQuery('')
    setOpen(false)
    setActiveIndex(0)
  }

  if (selected !== null) {
    return (
      <div className={styles['item-picker']}>
        <span className={styles['item-picker__label']} id={`${baseId}-label`}>{label}</span>
        <div className={styles['item-picker__chosen']} aria-labelledby={`${baseId}-label`} role="group">
          <CodeLabel code={selected.code} title={selected.title} known={selected.known} />
          <div className={styles['item-picker__chosen-actions']}>
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={() => onSelect(null)}
              aria-label={`${clearable ? 'Quitar' : 'Cambiar'} ${label.toLowerCase()}`}
              label={clearable ? 'Quitar' : 'Cambiar'}
            />
          </div>
        </div>
        {hint !== undefined && <p className={styles['item-picker__hint']}>{hint}</p>}
      </div>
    )
  }

  const listShown = open && term !== '' && query.trim() !== ''

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActiveIndex((index) => Math.min(index + 1, Math.max(matches.length - 1, 0)))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      // Never the form's submit: Enter in a search box means "this one".
      event.preventDefault()
      const item = matches[activeIndex]
      if (listShown && item !== undefined) {
        pick(item)
      }
    } else if (event.key === 'Escape' && listShown) {
      // Only the list: the dialog around the picker closes on the next Esc.
      event.stopPropagation()
      setOpen(false)
    }
  }

  const activeItem = listShown ? matches[activeIndex] : undefined

  return (
    <div className={styles['item-picker']}>
      <label className={styles['item-picker__label']} htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        className={styles['item-picker__field']}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={listShown}
        aria-controls={listId}
        aria-activedescendant={activeItem === undefined ? undefined : `${listId}-${activeItem.code}`}
        aria-describedby={hint !== undefined ? hintId : undefined}
        placeholder="Código o nombre…"
        value={query}
        disabled={disabled}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
          setActiveIndex(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {hint !== undefined && <p id={hintId} className={styles['item-picker__hint']}>{hint}</p>}

      {listShown && (
        <div className={styles['item-picker__popup']}>
          {search.isError
            ? <p role="alert" className={styles['item-picker__message']}>{trpcMessage(search.error)}</p>
            : null}
          {search.isPending && !search.isError
            ? <p role="status" className={styles['item-picker__message']}>Buscando…</p>
            : null}
          {search.isSuccess && matches.length === 0
            ? <p role="status" className={styles['item-picker__message']}>Ningún artículo coincide con «{term}».</p>
            : null}
          <ul id={listId} role="listbox" aria-label={label} className={styles['item-picker__list']}>
            {matches.map((item, index) => (
              <li
                key={item.code}
                id={`${listId}-${item.code}`}
                role="option"
                aria-selected={index === activeIndex}
                className={[
                  styles['item-picker__option'],
                  index === activeIndex ? styles['item-picker__option--active'] : '',
                ].filter(Boolean).join(' ')}
                // mousedown and not click: the input's blur closes the list
                // before a click would land on it.
                onMouseDown={(event) => {
                  event.preventDefault()
                  pick(item)
                }}
              >
                <CodeLabel code={item.code} title={item.title} known />
                {item.groupTitle !== null && (
                  <span className={styles['item-picker__group']}>{item.groupTitle}</span>
                )}
              </li>
            ))}
          </ul>
          {search.isSuccess && (
            <div className={styles['item-picker__footer']}>
              <FreshnessBadge freshness={search.data.freshness} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
